/**
 * Removing secrets from structured fields before they are written down.
 *
 * This runs on every record, which is the point: redaction that a caller has to
 * remember is redaction that eventually does not happen. A log group is read by
 * more people than the database it describes, retained for months, and — unlike
 * a database row — cannot be corrected after the fact. A leaked token in
 * CloudWatch is leaked until the retention window closes.
 *
 * It is a *net*, not a guarantee. It matches by key name, so a secret passed as
 * `{ value: 'ghp_…' }` sails through. Do not treat it as permission to pass
 * whole request bodies in.
 */
/**
 * Key names whose values never reach a log.
 *
 * Compared after normalisation (lowercased, `_`/`-`/spaces stripped), so one
 * entry covers `apiKey`, `api_key`, `API-KEY` and `api key`.
 */
export const DEFAULT_REDACTED_KEYS = [
    'password',
    'passwd',
    'pwd',
    'secret',
    'clientsecret',
    'token',
    'accesstoken',
    'refreshtoken',
    'idtoken',
    'sessiontoken',
    'authorization',
    'auth',
    'apikey',
    'apisecret',
    'cookie',
    'setcookie',
    'sessionid',
    'privatekey',
    'credentials',
    'ssn',
    'creditcard',
    'cardnumber',
    'cvv',
    'pin',
];
export const REDACTED = '[redacted]';
export const CIRCULAR = '[circular]';
export const TRUNCATED_SUFFIX = '…[truncated]';
export const DEFAULT_REDACT_OPTIONS = {
    maxDepth: 6,
    maxStringLength: 4096,
    maxArrayLength: 100,
    maxKeys: 64,
};
export function normalizeKey(key) {
    return key.toLowerCase().replace(/[\s_-]/g, '');
}
/**
 * A reusable redactor.
 *
 * Built once and shared, because the key set is turned into a `Set` and the
 * options are resolved here rather than per value — this runs on the hot path
 * of every single log call.
 */
export function createRedactor(options = {}) {
    const keys = new Set((options.keys ?? DEFAULT_REDACTED_KEYS).concat(options.extraKeys ?? []).map(normalizeKey));
    const maxDepth = options.maxDepth ?? DEFAULT_REDACT_OPTIONS.maxDepth;
    const maxStringLength = options.maxStringLength ?? DEFAULT_REDACT_OPTIONS.maxStringLength;
    const maxArrayLength = options.maxArrayLength ?? DEFAULT_REDACT_OPTIONS.maxArrayLength;
    const maxKeys = options.maxKeys ?? DEFAULT_REDACT_OPTIONS.maxKeys;
    const scrubStrings = options.scrubStrings;
    function walk(value, depth, seen) {
        if (value === null || value === undefined)
            return value;
        switch (typeof value) {
            case 'string': {
                const scrubbed = scrubStrings === undefined ? value : scrubStrings(value);
                return scrubbed.length > maxStringLength
                    ? scrubbed.slice(0, maxStringLength) + TRUNCATED_SUFFIX
                    : scrubbed;
            }
            case 'number':
                // JSON has no NaN or Infinity; they serialise to `null`, which reads as
                // "there was no value" rather than "the arithmetic went wrong".
                return Number.isFinite(value) ? value : String(value);
            case 'boolean':
                return value;
            case 'bigint':
                return `${value.toString()}n`;
            case 'function':
            case 'symbol':
                // Dropped. Neither survives JSON, and both are noise in a log.
                return undefined;
            default:
                break;
        }
        const object = value;
        if (seen.has(object))
            return CIRCULAR;
        if (depth >= maxDepth)
            return '[depth exceeded]';
        if (object instanceof Date)
            return object.toISOString();
        if (object instanceof RegExp)
            return object.toString();
        if (object instanceof Error) {
            // Errors are serialised properly one layer up, in `serializeError`. This
            // is the fallback for one buried inside `fields`.
            return { name: object.name, message: object.message };
        }
        seen.add(object);
        try {
            if (Array.isArray(object)) {
                const kept = object.slice(0, maxArrayLength).map((item) => walk(item, depth + 1, seen));
                if (object.length > maxArrayLength)
                    kept.push(`…${object.length - maxArrayLength} more`);
                return kept;
            }
            if (object instanceof Map) {
                return walk(Object.fromEntries(object), depth, seen);
            }
            if (object instanceof Set) {
                return walk([...object], depth, seen);
            }
            const entries = Object.entries(object);
            const result = {};
            for (const [key, item] of entries.slice(0, maxKeys)) {
                if (keys.has(normalizeKey(key))) {
                    result[key] = REDACTED;
                    continue;
                }
                const walked = walk(item, depth + 1, seen);
                // Preserve the difference between "absent" and "present but empty":
                // dropping undefined keeps the JSON line the same size either way.
                if (walked !== undefined)
                    result[key] = walked;
            }
            if (entries.length > maxKeys)
                result['…'] = `${entries.length - maxKeys} more keys`;
            return result;
        }
        finally {
            // Removed on the way out so a value that legitimately appears twice as
            // siblings is not reported as a cycle.
            seen.delete(object);
        }
    }
    return (value) => walk(value, 0, new Set());
}
/** The redactor used when a logger is not given one. */
export const defaultRedactor = createRedactor();
//# sourceMappingURL=redact.js.map